import { FC } from 'react';
import { PaymentFormField } from 'react-easyrocket';

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
    _smartico: any;
    _smartico_user_id: any;
    _smartico_language: any;
    LiveChatWidget: any;
  }
}

export type WithLanguage<T> = T & { language?: string };

export enum GameCategory {
  NewGames = 1,
  TopGames = 2,
  FeatureBuy = 579,
  Megaways = 580,
  BooksOfSlots = 581,
  ReadyToDrop = 582,
  Videoslot = 583,
  SportGames = 584,
  CasualGames = 585,
  FruitsGames = 586,
  DropWinsGames = 587
}

export type TimeLeft = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
};

export type UploadedFile = {
  responseImageUrl?: string;
  file?: File;
  imagePreviewUrl?: string | ArrayBuffer | null;
};

export const GamesCategoryName: { [key: number]: string } = {
  [GameCategory.NewGames]: 'New Games',
  [GameCategory.TopGames]: 'Top Games',
  [GameCategory.FeatureBuy]: 'Feature Buy',
  [GameCategory.Megaways]: 'Megaways',
  [GameCategory.BooksOfSlots]: 'Books Of Slots',
  [GameCategory.ReadyToDrop]: 'Ready To Drop',
  [GameCategory.Videoslot]: 'Videoslot',
  [GameCategory.SportGames]: 'Sport Games',
  [GameCategory.CasualGames]: 'Casual Games',
  [GameCategory.FruitsGames]: 'Fruits Games',
  [GameCategory.DropWinsGames]: 'Drop Wins Games'
};

export enum KycStatus {
  Pending = 0,
  NotVerified = 1,
  Verified = 2,
  DocumentsExpired = 3,
  Less18Years = 4
}

export const KYC_STATUS_NAME: { [key: number]: string } = {
  [KycStatus.Pending]: 'kycStatus.pending',
  [KycStatus.NotVerified]: 'kycStatus.notVerified',
  [KycStatus.Verified]: 'kycStatus.verified',
  [KycStatus.DocumentsExpired]: 'kycStatus.documentsExpired',
  [KycStatus.Less18Years]: 'kycStatus.less18Years'
};

export enum BONUS_TYPE {
  Registration = 0,
  RegistrationPromoCode = 1,
  FirstAuthorization = 2,
  PromoCode = 3,
  Birthday = 4,
  Deposits = 5,
  Periodic = 6,
  IncreaseWinnings = 7,
  ActivatedByLink = 8
}

export type Country = {
  name: string;
  alpha2: string;
  alpha3: string;
  flag: string;
  timezones: {
    gmtOffset: number;
    gmtOffsetName: string;
    zoneName: string;
  }[];
  currencies: string[];
  phoneCodes: string[];
};

export type DropDownOption = { id: string; name: string };

export type StepProps = {
  changeStep: (value: number) => void;
  availableSteps: boolean[];
  completedSteps: boolean[];
};

export type StepsOption = {
  title: string;
  children: FC<StepProps>;
};

export enum BonusTransactionType {
  Bonus = 0,
  MoneyPrize = 1,
  Manual = 2,
  Referral = 3,
  FreeSpinWin = 4,
  Bet = 5,
  BetSport = 6,
  Win = 7,
  Zeroing = 8,
  CancelWager = 9,
  WinSport = 10,
  CashBack = 11,
  LevelBonus = 12,
  Refund = 13,
  RollbackBet = 14,
  RollbackWin = 15
}

export const BONUS_TRANSACTION_TYPE: string[] = [
  'arrayTransactions.bonus',
  'arrayTransactions.moneyPrize',
  'bonusType.manualBonus',
  'arrayTransactions.referral',
  'arrayTransactions.freeSpinWin',
  'arrayTransactions.bet',
  'arrayTransactions.betSport',
  'arrayTransactions.win',
  'arrayTransactions.zeroing',
  'arrayTransactions.cancelWager',
  'arrayTransactions.winSport',
  'arrayTransactions.cashback',
  'arrayTransactions.levelBonus',
  'arrayTransactions.refund',
  'arrayTransactions.rollbackBet',
  'arrayTransactions.rollbackWin',
  'bonusType.jackpotWin'
];

export type GetTransactionsRequestBody = {
  'amount-from'?: string;
  'amount-to'?: string;
  'date-start'?: string;
  'date-end'?: string;
  type?: string;
  sort?: string;
  'sort-dir'?: string;
  page?: number;
  status?: string;
};

export enum StatusBonusTransaction {
  Pending = 0,
  Queue = 1,
  Credited = 2,
  Activated = 3,
  Rejected = 4,
  Cancel = 5,
  Complete = 6,
  Refund = 7
}

export const STATUS_BONUS_TRANSACTION_NAME: { [key: number]: string } = {
  [StatusBonusTransaction.Pending]: 'transactionStatus.pending',
  [StatusBonusTransaction.Queue]: 'transactionStatus.queue',
  [StatusBonusTransaction.Credited]: 'transactionStatus.credited',
  [StatusBonusTransaction.Activated]: 'transactionStatus.activated',
  [StatusBonusTransaction.Rejected]: 'transactionStatus.rejected',
  [StatusBonusTransaction.Cancel]: 'transactionStatus.cancel',
  [StatusBonusTransaction.Complete]: 'transactionStatus.complete',
  [StatusBonusTransaction.Refund]: 'transactionStatus.refund'
};

export const TRANSACTION_STATUS: { [key: string]: string } = {
  '0': 'transactionStatus.pending',
  '1': 'transactionStatus.complete',
  '2': 'transactionStatus.cancel',
  '3': 'transactionStatus.failed',
  '4': 'transactionStatus.refund',
  '5': 'transactionStatus.pendingWager',
  '6': 'transactionStatus.rejected',
  '7': 'transactionStatus.notConfirmed',
  '8': 'transactionStatus.processing',
  '9': 'transactionStatus.aggregatorRejected',
  '10': 'transactionStatus.rollback',
  '11': 'transactionStatus.wagerZeroing',
  '12': 'transactionStatus.notConfirmedBlockAmount'
};

export const TRANSACTION_TYPE: string[] = [
  'arrayTransactions.deposit',
  'arrayTransactions.withdraw',
  'arrayTransactions.bet',
  'arrayTransactions.win',
  'arrayTransactions.bonus',
  'arrayTransactions.jackpot',
  'arrayTransactions.changeBalanceInc',
  'arrayTransactions.changeBalanceDec',
  'arrayTransactions.referral',
  'arrayTransactions.convertBalance',
  'arrayTransactions.moneyPrize',
  'arrayTransactions.bonusTransaction',
  'arrayTransactions.cancelBonusFine',
  'arrayTransactions.refund',
  'arrayTransactions.rollbackBet',
  'arrayTransactions.rollbackWin',
  'arrayTransactions.betSport',
  'arrayTransactions.jackpotIncreasing',
  'arrayTransactions.freeSpinWin',
  'arrayTransactions.zeroing',
  'arrayTransactions.winSport',
  'arrayTransactions.increment',
  'arrayTransactions.cashBack',
  'arrayTransactions.levelBonus'
];

export type PaymentMethod = {
  success: boolean;
  type: string;
  order: string;
  method: string;
  title: string;
  logoUrl: string;
  options?: PaymentMethodOption[];
  formFields?: PaymentFormField[];
  min_amount: string;
  max_amount: string;
  commission: string;
  commission_payer: number;
  exchangeCurrency?: string;
  exchangeRate?: string;
  _reactDepositMethodId?: number;
};

export type PaymentMethodOption = {
  name: string;
  title: string;
  exchangeRate: string;
  amount?: number;
  currency?: string;
  id?: number;
};

export type ResponsiveBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'gf';

export enum BonusType {
  'All' = 0,
  'Casino' = 1,
  'Sport' = 2
}

export type GameCategoriesMap = Record<
  string,
  { name: string; title: string; value: number | boolean }
>;

export type HellocleverResponse = {
  success: boolean;
  currency: string;
  amount: string;
  orderId: number;
  account: string;
  nonce: string;
};

export interface SVGPropsIcon {
  className?: string;
  onClick?: () => void;
  fill?: string;
  style?: React.CSSProperties;
  onShow?: ((e: any) => void) | undefined;
  closeTopDrawer?: (() => void) | undefined;
  setPasswordShown?: (value: React.SetStateAction<boolean>) => void;
  passwordShown?: boolean;
  stroke?: string;
  isGame?: boolean;
  isFavorite?: boolean;
}
